/**
 * Holds a scroll position across a window that temporarily shortens the
 * document — `usePageBoundaries.measure()`'s strip → read → apply pass, which
 * removes every injected page gap (thousands of px on a real One Pager) before
 * reading geometry. While the document is that short the browser clamps
 * `scrollTop` into the shortened scroll range; today it comes back only because
 * the re-apply lands in the same synchronous task.
 *
 * The guard restores ONLY a position the browser clamped, never any upward
 * movement: a clamp lands exactly at the shortened `scrollHeight - clientHeight`,
 * so that is the signature `noteShortWindow()` looks for. Restoring on upward
 * movement alone would fight a reader scrolling up — the very symptom this
 * family of fixes is about.
 *
 * Plate's `useScrollRef` would be the idiomatic way to name the scroller, but it
 * is only populated by Plate's own `<EditorContainer>`, which this editor does
 * not render — and the One Pager's real scroller is the drawer canvas, an
 * ancestor outside the Plate tree. Hence the DOM walk.
 */

/** Sub-pixel `scrollTop` drift (zoom / DPR) is not a clamp and must not trigger a write. */
const SCROLL_RESTORE_EPSILON_PX = 1;

function isScrollableY(el: HTMLElement, view: Window): boolean {
    const { overflowY } = view.getComputedStyle(el);
    if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false;
    return el.scrollHeight - el.clientHeight > SCROLL_RESTORE_EPSILON_PX;
}

/**
 * The nearest ancestor (or `from` itself) that is a real vertical scroller, else
 * the document scroller when the page itself scrolls, else `null`. Covers all
 * three editor surfaces: the One Pager drawer canvas (an ancestor), the bounded
 * comments box (the content wrapper itself), and a full-page report.
 */
export function findScrollContainer(from: HTMLElement | null | undefined): HTMLElement | null {
    if (!from) return null;
    const doc = from.ownerDocument;
    const view = doc?.defaultView;
    if (!doc || !view) return null;

    let el: HTMLElement | null = from;
    while (el && el !== doc.documentElement) {
        if (isScrollableY(el, view)) return el;
        el = el.parentElement;
    }
    // The document scroller computes as `overflow: visible`, so it can only be
    // recognised by whether it overflows.
    const scrollingEl = doc.scrollingElement as HTMLElement | null;
    if (scrollingEl && scrollingEl.scrollHeight - scrollingEl.clientHeight > SCROLL_RESTORE_EPSILON_PX) return scrollingEl;
    return null;
}

export interface PreservedScrollTop {
    /** The guarded scroller — `null` when nothing scrolls, in which case every method is a no-op. */
    readonly scroller: HTMLElement | null;
    /** `scrollTop` at capture time; 0 with no scroller. */
    readonly capturedPx: number;
    /**
     * Call once inside the shortened window (right after the document was made
     * shorter). Records a clamp — and only a clamp — so `restore()` knows there
     * is something to put back. Returns the px clamped away, 0 if nothing was.
     */
    noteShortWindow(): number;
    /** Put a clamped position back. No clamp recorded, or the position already moved on → no write. Returns px restored. */
    restore(): number;
}

/**
 * Capture `scroller`'s `scrollTop` so a browser clamp during a shortening window
 * can be undone.
 *
 * Usage: capture → shorten the document → `noteShortWindow()` → restore the
 * document → `restore()`.
 */
export function preserveScrollTop(scroller: HTMLElement | null | undefined): PreservedScrollTop {
    const target = scroller ?? null;
    const capturedPx = target ? target.scrollTop : 0;
    let clampedToPx: number | null = null;

    return {
        scroller: target,
        capturedPx,
        noteShortWindow(): number {
            if (!target) return 0;
            const currentPx = target.scrollTop;
            const lostPx = capturedPx - currentPx;
            if (lostPx < SCROLL_RESTORE_EPSILON_PX) return 0;
            // A clamp always lands exactly on the shortened maximum. An upward
            // user scroll lands wherever the user stopped, so it fails this test
            // and is left alone.
            const shortenedMaxPx = Math.max(0, target.scrollHeight - target.clientHeight);
            if (Math.abs(currentPx - shortenedMaxPx) > SCROLL_RESTORE_EPSILON_PX) return 0;
            clampedToPx = currentPx;
            return Math.round(lostPx);
        },
        restore(): number {
            if (!target || clampedToPx === null) return 0;
            // Anything that moved the position since the clamp — the browser
            // healing it itself, or the user — owns it now.
            if (target.scrollTop > clampedToPx + SCROLL_RESTORE_EPSILON_PX) return 0;
            const lostPx = capturedPx - target.scrollTop;
            if (lostPx < SCROLL_RESTORE_EPSILON_PX) return 0;
            target.scrollTop = capturedPx;
            return Math.round(lostPx);
        },
    };
}
